import { REALTIME_EVENT_TYPES, getEnvelopeRfqUlid } from './types';

export const OPEN_KNOTS_LIFECYCLE_TOAST_AUTO_CLOSE_MS = 3000;

/** APPLICATIO-3712: toast / bell heading when supplier confirms a counter offer. */
export const OPEN_KNOTS_SUPPLIER_CONFIRMED_BID_TITLE = 'Supplier confirmed the bid';

const SUPPLIER_COUNTER_CONFIRM_MESSAGE = 'accepted the counter offer';

function isSupplierCounterConfirmMessage(message) {
  return String(message || '')
    .toLowerCase()
    .includes(SUPPLIER_COUNTER_CONFIRM_MESSAGE);
}

function resolveOpenKnotsNotificationToastTitle(title, message) {
  if (isSupplierCounterConfirmMessage(message)) {
    return OPEN_KNOTS_SUPPLIER_CONFIRMED_BID_TITLE;
  }
  return title;
}

export const OPEN_KNOTS_BID_LIFECYCLE_EVENTS = [
  REALTIME_EVENT_TYPES.RFQ_BID_SUBMITTED,
  REALTIME_EVENT_TYPES.RFQ_BID_UPDATED,
  REALTIME_EVENT_TYPES.RFQ_BID_WITHDRAWN,
  REALTIME_EVENT_TYPES.RFQ_BID_ACCEPTED,
  REALTIME_EVENT_TYPES.RFQ_BID_CONFIRMED,
  REALTIME_EVENT_TYPES.RFQ_BID_DECLINED,
  REALTIME_EVENT_TYPES.RFQ_COUNTER_CREATED,
  REALTIME_EVENT_TYPES.RFQ_COUNTER_REVISED,
  REALTIME_EVENT_TYPES.RFQ_COUNTER_EXPIRED,
  REALTIME_EVENT_TYPES.RFQ_BID_EXPIRED,
];

const OPEN_KNOTS_BID_NOTIFICATION_NAMES = new Set([
  'bid received',
  'bid updated',
  'bid withdrawn',
  'bid confirmed',
  'bid accepted',
  'buyer accepted bid',
  'rfq declined',
  'counter offer send',
  'counter offer revised',
  'openknots bid expiry',
  'openknots rfq expiry',
  'rfq closed',
]);

function normalizeBidNotificationName(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/_/g, ' ');
}

function isTruthyOpenKnotsFlag(payload) {
  return payload.isOpenknots === true || payload.isOpenknots === 'true' || payload.is_openknots === true;
}

function enquiryPhrase(payload) {
  const id = payload && (payload.rfqId ?? payload.rfq_id);
  if (id === undefined || id === null || id === '') return 'for enquiry';
  return `for enquiry (RFQ ID: ${id})`;
}

function eventTitle(payload, fallback) {
  const name = payload && (payload.eventName || payload.event_name);
  return typeof name === 'string' && name.trim() ? name.trim() : fallback;
}

const recentLifecycleToasts = new Map();
const LIFECYCLE_TOAST_DEDUPE_MS = 15000;

/** Skip a second count/row toast for the same enquiry event that the socket just showed. */
export function rememberOpenKnotsLifecycleToast(key) {
  if (!key) return;
  recentLifecycleToasts.set(key, Date.now());
}

export function hasRecentOpenKnotsLifecycleToast(key) {
  if (!key) return false;
  const seenAt = recentLifecycleToasts.get(key);
  return typeof seenAt === 'number' && Date.now() - seenAt < LIFECYCLE_TOAST_DEDUPE_MS;
}

/**
 * True when notification.created payload is an Open Knots bid / RFQ lifecycle row.
 * @param {unknown} payload
 */
export function isOpenKnotsBidNotificationPayload(payload) {
  if (!payload || typeof payload !== 'object') return false;
  if (!isTruthyOpenKnotsFlag(payload)) return false;
  const name = normalizeBidNotificationName(
    payload.eventName || payload.notificationType || payload.event_name
  );
  if (OPEN_KNOTS_BID_NOTIFICATION_NAMES.has(name)) return true;
  if (name.startsWith('rfq closed')) return true;
  return false;
}

const OPEN_KNOTS_RFQ_EXPIRY_EVENT_NAME = 'openknots rfq expiry';

/**
 * True when notification.created payload is Open Knots RFQ-level expiry (cron, no rfq.bid.* follow-up).
 * @param {unknown} payload
 */
export function isOpenKnotsRfqExpiryNotificationPayload(payload) {
  if (!isOpenKnotsBidNotificationPayload(payload)) return false;
  const name = normalizeBidNotificationName(payload.eventName || payload.notificationType);
  return name === OPEN_KNOTS_RFQ_EXPIRY_EVENT_NAME;
}

/**
 * Pick the RFQ expiry row from getNewNotifications after notification.created.
 * @param {unknown[]} notifications
 * @param {string} [rfqUlid]
 */
export function findOpenKnotsRfqExpiryNotification(notifications, rfqUlid) {
  if (!Array.isArray(notifications)) return null;
  const targetUlid = rfqUlid ? String(rfqUlid) : '';
  for (const notification of notifications) {
    if (!isOpenKnotsBidNotificationRecord(notification)) continue;
    const names = notificationRecordNames(notification);
    if (!names.includes(OPEN_KNOTS_RFQ_EXPIRY_EVENT_NAME)) continue;
    if (targetUlid) {
      const rowUlid = notification.rfq_ulid ?? notification.rfqUlid ?? '';
      if (rowUlid && String(rowUlid) !== targetUlid) continue;
    }
    return notification;
  }
  return null;
}

/**
 * Latest Open Knots RFQ award / close row for a buyer toast (supplier counter confirm or buyer accept).
 * @param {unknown[]} notifications
 * @param {string} [rfqUlid]
 */
export function findOpenKnotsBidAwardNotification(notifications, rfqUlid) {
  if (!Array.isArray(notifications)) return null;
  const targetUlid = rfqUlid ? String(rfqUlid) : '';
  for (const notification of notifications) {
    if (!isOpenKnotsBidNotificationRecord(notification)) continue;
    if (targetUlid) {
      const rowUlid = notification.rfq_ulid ?? notification.rfqUlid ?? '';
      if (rowUlid && String(rowUlid) !== targetUlid) continue;
    }
    const names = notificationRecordNames(notification);
    const isAward = names.some(
      (name) =>
        name === 'bid accepted' ||
        name === 'buyer accepted bid' ||
        name === 'rfq closed' ||
        name.startsWith('rfq closed')
    );
    if (!isAward) continue;
    return notification;
  }
  return null;
}

/**
 * @param {unknown} payload
 * @returns {string}
 */
export function getSupplierDisplayName(payload) {
  if (!payload || typeof payload !== 'object') return 'A supplier';
  const name = payload.supplierName || payload.supplier_name;
  return typeof name === 'string' && name.trim() ? name.trim() : 'A supplier';
}

/**
 * Domain toasts for Open Knots bid revision / withdrawal / award (APPLICATIO-3031/3032/3038).
 * @param {unknown} envelope
 * @returns {{ level: 'info'|'success', title: string, message: string, toastId: string, rfqUlid: string, dedupeKey: string }|null}
 */
export function getOpenKnotsBidLifecycleToast(envelope) {
  const type = envelope && typeof envelope === 'object' ? envelope.eventType : '';
  const payload = envelope && typeof envelope === 'object' ? envelope.payload : null;
  const rfqUlid = getEnvelopeRfqUlid(envelope);
  const name = getSupplierDisplayName(payload);
  const toastId = `${type}-${rfqUlid || envelope?.correlationId || 'openknots'}`;
  const dedupeKey = `${type}:${rfqUlid || envelope?.correlationId || ''}`;
  const enquiry = enquiryPhrase(payload);

  if (type === REALTIME_EVENT_TYPES.RFQ_BID_SUBMITTED) {
    return {
      level: 'info',
      title: eventTitle(payload, 'Bid Received'),
      message: `${name} has submitted a bid ${enquiry}`,
      toastId,
      rfqUlid,
      dedupeKey,
    };
  }
  if (type === REALTIME_EVENT_TYPES.RFQ_BID_UPDATED) {
    return {
      level: 'info',
      title: eventTitle(payload, 'Bid Updated'),
      message: `${name} has updated their bid ${enquiry}`,
      toastId,
      rfqUlid,
      dedupeKey,
    };
  }
  if (type === REALTIME_EVENT_TYPES.RFQ_BID_WITHDRAWN) {
    return {
      level: 'info',
      title: eventTitle(payload, 'Bid Withdrawn'),
      message: `${name} withdrew their bid ${enquiry}`,
      toastId,
      rfqUlid,
      dedupeKey,
    };
  }
  if (type === REALTIME_EVENT_TYPES.RFQ_BID_ACCEPTED) {
    return {
      level: 'success',
      title: eventTitle(payload, 'Bid Accepted'),
      message: `${name}'s bid has been awarded. The RFQ is now closed.`,
      toastId: rfqUlid ? `openknots-rfq-accepted-${rfqUlid}` : toastId,
      rfqUlid,
      dedupeKey,
    };
  }
  if (type === REALTIME_EVENT_TYPES.RFQ_BID_CONFIRMED) {
    return {
      level: 'success',
      title: eventTitle(payload, OPEN_KNOTS_SUPPLIER_CONFIRMED_BID_TITLE),
      message: `${name} confirmed their bid ${enquiry}`,
      toastId,
      rfqUlid,
      dedupeKey,
    };
  }
  if (type === REALTIME_EVENT_TYPES.RFQ_BID_DECLINED) {
    return {
      level: 'info',
      title: eventTitle(payload, 'RFQ Declined'),
      message: `${name} declined the enquiry`,
      toastId,
      rfqUlid,
      dedupeKey,
    };
  }
  if (type === REALTIME_EVENT_TYPES.RFQ_COUNTER_CREATED || type === REALTIME_EVENT_TYPES.RFQ_COUNTER_REVISED) {
    const revised = type === REALTIME_EVENT_TYPES.RFQ_COUNTER_REVISED;
    return {
      level: 'info',
      title: eventTitle(payload, revised ? 'Counter Offer Revised' : 'Counter Offer Send'),
      message: revised ? `Counter offer to ${name} was revised` : `Counter offer sent to ${name}`,
      toastId,
      rfqUlid,
      dedupeKey,
    };
  }
  if (type === REALTIME_EVENT_TYPES.RFQ_COUNTER_EXPIRED) {
    return {
      level: 'info',
      title: eventTitle(payload, 'Counter Offer Expired'),
      message: `Counter offer to ${name} has expired`,
      toastId,
      rfqUlid,
      dedupeKey,
    };
  }
  if (type === REALTIME_EVENT_TYPES.RFQ_BID_EXPIRED) {
    return {
      level: 'info',
      title: eventTitle(payload, 'Openknots Bid expiry'),
      message: `${name}'s bid has expired`,
      toastId,
      rfqUlid,
      dedupeKey,
    };
  }
  return null;
}

const NOTIFICATION_TYPE_TO_EVENT = {
  'bid received': REALTIME_EVENT_TYPES.RFQ_BID_SUBMITTED,
  'bid updated': REALTIME_EVENT_TYPES.RFQ_BID_UPDATED,
  'bid withdrawn': REALTIME_EVENT_TYPES.RFQ_BID_WITHDRAWN,
  'bid confirmed': REALTIME_EVENT_TYPES.RFQ_BID_CONFIRMED,
  'bid accepted': REALTIME_EVENT_TYPES.RFQ_BID_ACCEPTED,
  'buyer accepted bid': REALTIME_EVENT_TYPES.RFQ_BID_ACCEPTED,
  'rfq declined': REALTIME_EVENT_TYPES.RFQ_BID_DECLINED,
  'counter offer send': REALTIME_EVENT_TYPES.RFQ_COUNTER_CREATED,
  'counter offer revised': REALTIME_EVENT_TYPES.RFQ_COUNTER_REVISED,
  'openknots bid expiry': REALTIME_EVENT_TYPES.RFQ_BID_EXPIRED,
};

function notificationRecordNames(notification) {
  const payload = notification?.payload;
  return [
    notification?.notification_type,
    notification?.notificationType,
    payload?.event,
    payload?.eventName,
  ].map(normalizeBidNotificationName);
}

/**
 * Bell-dropdown row for an Open Knots bid notification (API shape, not the WS envelope).
 * @param {unknown} notification
 */
export function isOpenKnotsBidNotificationRecord(notification) {
  if (!notification || typeof notification !== 'object') return false;
  const flag = notification.is_openknots ?? notification.isOpenknots;
  if (flag !== true && flag !== 'true') return false;
  return notificationRecordNames(notification).some(
    (name) => OPEN_KNOTS_BID_NOTIFICATION_NAMES.has(name) || name.startsWith('rfq closed')
  );
}

/**
 * Toast matching the notification dropdown title + body, once the row has been fetched.
 * @param {unknown} notification
 */
export function getOpenKnotsToastFromNotificationRecord(notification) {
  if (!isOpenKnotsBidNotificationRecord(notification)) return null;
  const payload = notification.payload && typeof notification.payload === 'object' ? notification.payload : {};
  const message = typeof payload.notification === 'string' ? payload.notification.trim() : '';
  if (!message) return null;

  const matchedName = notificationRecordNames(notification).find(
    (name) => OPEN_KNOTS_BID_NOTIFICATION_NAMES.has(name) || name.startsWith('rfq closed')
  );
  const eventType = NOTIFICATION_TYPE_TO_EVENT[matchedName] || REALTIME_EVENT_TYPES.NOTIFICATION_CREATED;
  const rfqUlid =
    (typeof notification.rfq_ulid === 'string' && notification.rfq_ulid) ||
    (typeof notification.rfqUlid === 'string' && notification.rfqUlid) ||
    '';
  const rawTitle = typeof payload.event === 'string' && payload.event.trim() ? payload.event.trim() : '';
  const title = resolveOpenKnotsNotificationToastTitle(rawTitle, message);
  const toastId =
    eventType === REALTIME_EVENT_TYPES.RFQ_BID_ACCEPTED && rfqUlid
      ? `openknots-rfq-accepted-${rfqUlid}`
      : `${eventType}-${rfqUlid || notification.ulid || 'openknots'}`;

  return {
    level:
      eventType === REALTIME_EVENT_TYPES.RFQ_BID_ACCEPTED || eventType === REALTIME_EVENT_TYPES.RFQ_BID_CONFIRMED
        ? 'success'
        : 'info',
    title,
    message,
    toastId,
    rfqUlid,
    dedupeKey: `${eventType}:${rfqUlid}`,
  };
}
